import React, { useState } from 'react';
import {
  Drawer, List, ListItemText, ListItemIcon, CssBaseline, Box, IconButton, Collapse, ListItemButton,
  Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation } from 'react-router-dom';
import ViewCompletedOperations from './ViewCompletedOperations';
import LogoutButton from './LogoutButton';
import SummarySection from './SummarySection';
import GeneratePlanvsActual from './GeneratePlanvsActual';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ViewOrDownloadPlanVsActual from './ViewOrDownloadPlanVsActual';
import MachineHourApproval from './MachineHourApproval';

const drawerWidth = 240;
const drawerWidthMinimized = 72;

function HrDashbord() {
  const [isMinimized, setIsMinimized] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [subTabValue, setSubTabValue] = useState(0);
  const location = useLocation();
  const { username } = location.state || {};

  const toggleDrawer = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleSubMenu = () => {
    setIsSubMenuOpen(!isSubMenuOpen);
  };

  const sidebarItems = [
    { text: 'Summary', icon: <DashboardIcon />, tabIndex: 0 },
    { text: 'Plan Vs Actual', icon: <CompareArrowsIcon />, tabIndex: 1, hasSubMenu: true },
    { text: 'Settings', icon: <SettingsIcon />, tabIndex: 2 },
    { text: 'Reports', icon: <AssessmentIcon />, tabIndex: 3 },
    { text: 'Completed Operations', icon: <CheckCircleIcon />, tabIndex: 4 },
    { text: 'Approval', icon: <HowToRegIcon />, tabIndex: 5 },
  ];

  const handleTabChange = (index) => {
    setTabValue(index);
    if (index !== 1) {
      setIsSubMenuOpen(false);
    }
  };

  const handleSubTabChange = (index) => {
    setSubTabValue(index);
  };


  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* ✅ UPDATED: Sidebar with hamburger menu at top */}
      <Drawer
        variant="permanent"
        sx={{
          width: isMinimized ? drawerWidthMinimized : drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isMinimized ? drawerWidthMinimized : drawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: 'width 0.3s',
          },
        }}
      >
        {/* ✅ NEW: Hamburger menu button at top of sidebar */}
        <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
          <IconButton 
            onClick={toggleDrawer} 
            sx={{ 
              width: '100%', 
              justifyContent: isMinimized ? 'center' : 'flex-start',
              color: 'primary.main'
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <List>
          {sidebarItems.map((item) => (
            <React.Fragment key={item.tabIndex}>
              <ListItemButton
                onClick={() => {
                  handleTabChange(item.tabIndex);
                  if (item.hasSubMenu) toggleSubMenu();
                }}
                selected={tabValue === item.tabIndex}
                sx={{
                  justifyContent: isMinimized ? 'center' : 'initial',
                  backgroundColor: tabValue === item.tabIndex ? 'rgba(25, 118, 210, 0.12)' : 'inherit',
                  '&:hover': {
                    backgroundColor: tabValue === item.tabIndex ? 'rgba(25, 118, 210, 0.16)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: isMinimized ? 0 : 40, 
                  color: tabValue === item.tabIndex ? 'primary.main' : 'inherit',
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </ListItemIcon>
                {!isMinimized && (
                  <ListItemText
                    primary={item.text}
                    sx={{ 
                      color: tabValue === item.tabIndex ? 'primary.main' : 'inherit',
                      fontWeight: tabValue === item.tabIndex ? 600 : 400
                    }}
                  />
                )}
                {item.hasSubMenu && !isMinimized && (
                  <Box sx={{ color: tabValue === item.tabIndex ? 'primary.main' : 'inherit' }}>
                    {isSubMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                )}
              </ListItemButton>
              
              {/* ✅ ENHANCED: Submenu with better styling */}
              {item.hasSubMenu && (
                <Collapse in={isSubMenuOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton
                      onClick={() => handleSubTabChange(0)}
                      selected={subTabValue === 0}
                      sx={{ 
                        pl: 4, 
                        justifyContent: isMinimized ? 'center' : 'initial',
                        backgroundColor: subTabValue === 0 ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                        '&:hover': {
                          backgroundColor: subTabValue === 0 ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: isMinimized ? 0 : 40,
                        justifyContent: 'center'
                      }}>
                        <EditIcon color={subTabValue === 0 ? 'primary' : 'inherit'} />
                      </ListItemIcon>
                      {!isMinimized && (
                        <ListItemText 
                          primary="Generate and Edit" 
                          sx={{ 
                            color: subTabValue === 0 ? 'primary.main' : 'inherit',
                            fontWeight: subTabValue === 0 ? 500 : 400
                          }}
                        />
                      )}
                    </ListItemButton>
                    <ListItemButton
                      onClick={() => handleSubTabChange(1)}
                      selected={subTabValue === 1}
                      sx={{ 
                        pl: 4, 
                        justifyContent: isMinimized ? 'center' : 'initial',
                        backgroundColor: subTabValue === 1 ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                        '&:hover': {
                          backgroundColor: subTabValue === 1 ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: isMinimized ? 0 : 40,
                        justifyContent: 'center'
                      }}>
                        <ViewCarouselIcon color={subTabValue === 1 ? 'primary' : 'inherit'} />
                      </ListItemIcon>
                      {!isMinimized && (
                        <ListItemText 
                          primary="View or Download" 
                          sx={{ 
                            color: subTabValue === 1 ? 'primary.main' : 'inherit',
                            fontWeight: subTabValue === 1 ? 500 : 400
                          }}
                        />
                      )}
                    </ListItemButton>
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>

        {/* ✅ ENHANCED: Better logout button placement */}
        {!isMinimized && (
          <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid #e0e0e0' }}>
            <LogoutButton username={username} />
          </Box>
        )}
      </Drawer>

      {/* Main content area — width constrained to viewport minus sidebar */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2.5,
          transition: 'width 0.3s, margin-left 0.3s',
          minHeight: '100vh',
          backgroundColor: '#fafafa',
          /* key fix: prevent content overflowing past viewport */
          width: `calc(100vw - ${isMinimized ? drawerWidthMinimized : drawerWidth}px)`,
          minWidth: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {tabValue === 0 && (
          <Box sx={{ width: '100%' }}>
            <SummarySection />
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ width: '100%' }}>
            {subTabValue === 0 && <GeneratePlanvsActual />}
            {subTabValue === 1 && <ViewOrDownloadPlanVsActual />}
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography variant="h4" gutterBottom>Settings</Typography>
            <Typography variant="body1" color="text.secondary">
              Settings panel coming soon...
            </Typography>
          </Box>
        )}

        {tabValue === 3 && (
          <Box>
            <Typography variant="h4" gutterBottom>Reports</Typography>
            <Typography variant="body1" color="text.secondary">
              Reports section coming soon...
            </Typography>
          </Box>
        )}

        {tabValue === 4 && (
          <Box>
            <ViewCompletedOperations />
          </Box>
        )}

        {tabValue === 5 && (
          <Box>
            <MachineHourApproval username={username} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default HrDashbord;